import React, { useCallback, useEffect, useState } from 'react'
import { supabase } from '../supabaseClient'

const RESOURCE_TYPES = ['Link', 'Document', 'Board', 'Video', 'Other']
const RESOURCE_CATEGORIES = ['Training', 'Travel', 'Nutrition', 'Recovery', 'Mindset', 'Community', 'Other']

const SETUP_SQL = `create table if not exists public.resources (
  id bigint generated by default as identity primary key,
  title text not null,
  description text,
  url text not null,
  resource_type text not null default 'Link',
  category text not null default 'Training',
  active boolean not null default true,
  created_by uuid references auth.users(id) on delete set null,
  created_at timestamptz not null default now()
);

alter table public.resources enable row level security;

drop policy if exists "Resources are visible to signed in users" on public.resources;
create policy "Resources are visible to signed in users"
on public.resources for select
to authenticated
using (active = true);

drop policy if exists "Coaches can create resources" on public.resources;
create policy "Coaches can create resources"
on public.resources for insert
to authenticated
with check (
  exists (
    select 1 from public.profiles
    where profiles.id = auth.uid()
    and profiles.role = 'coach'
  )
);

drop policy if exists "Coaches can update resources" on public.resources;
create policy "Coaches can update resources"
on public.resources for update
to authenticated
using (
  exists (
    select 1 from public.profiles
    where profiles.id = auth.uid()
    and profiles.role = 'coach'
  )
)
with check (
  exists (
    select 1 from public.profiles
    where profiles.id = auth.uid()
    and profiles.role = 'coach'
  )
);

drop policy if exists "Coaches can delete resources" on public.resources;
create policy "Coaches can delete resources"
on public.resources for delete
to authenticated
using (
  exists (
    select 1 from public.profiles
    where profiles.id = auth.uid()
    and profiles.role = 'coach'
  )
);`

function normalizeUrl(value) {
  const trimmed = String(value || '').trim()
  if (!trimmed) return ''
  if (/^https?:\/\//i.test(trimmed)) return trimmed
  return `https://${trimmed}`
}

function resourceIcon(type) {
  if (type === 'Document') return '▣'
  if (type === 'Board') return '⌘'
  if (type === 'Video') return '▶'
  if (type === 'Other') return '✦'
  return '↗'
}

export default function Resources({ user, profile }) {
  const isCoach = profile?.role === 'coach'
  const [resources, setResources] = useState([])
  const [loading, setLoading] = useState(true)
  const [tableMissing, setTableMissing] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [url, setUrl] = useState('')
  const [resourceType, setResourceType] = useState('Link')
  const [category, setCategory] = useState('Training')
  const [toast, setToast] = useState(null)

  const showToast = msg => { setToast(msg); setTimeout(() => setToast(null), 2500) }

  const fetchResources = useCallback(async () => {
    setLoading(true)
    const { data, error } = await supabase
      .from('resources')
      .select('*')
      .eq('active', true)
      .order('created_at', { ascending: false })

    if (error) {
      setResources([])
      setTableMissing(error.code === '42P01' || /resources/i.test(error.message || ''))
      setLoading(false)
      return
    }

    setTableMissing(false)
    setResources(data || [])
    setLoading(false)
  }, [])

  useEffect(() => { fetchResources() }, [fetchResources])

  const addResource = async () => {
    if (!title.trim() || !url.trim()) {
      showToast('Title and link are required')
      return
    }

    const { error } = await supabase.from('resources').insert({
      title: title.trim(),
      description: description.trim(),
      url: normalizeUrl(url),
      resource_type: resourceType,
      category,
      created_by: user.id
    })

    if (error) {
      showToast('Error: ' + error.message)
      return
    }

    setTitle('')
    setDescription('')
    setUrl('')
    setResourceType('Link')
    setCategory('Training')
    setShowForm(false)
    fetchResources()
    showToast('Resource added')
  }

  const removeResource = async (id) => {
    const { error } = await supabase.from('resources').update({ active: false }).eq('id', id)
    if (error) showToast('Error: ' + error.message)
    else {
      fetchResources()
      showToast('Resource removed')
    }
  }

  const grouped = resources.reduce((map, resource) => {
    const key = resource.category || 'Other'
    if (!map[key]) map[key] = []
    map[key].push(resource)
    return map
  }, {})

  return (
    <div>
      <div className="section-header">
        <div>
          <h2 className="section-title">Resources</h2>
          <p style={{ color: 'var(--charcoal-light)', fontSize: '14px', lineHeight: 1.5, marginTop: '6px' }}>
            Training links, travel workouts, boards, documents, and athlete reference material.
          </p>
        </div>
        {isCoach && !tableMissing && (
          <button className="btn-sm" onClick={() => setShowForm(open => !open)}>
            {showForm ? 'Close' : '+ Add Resource'}
          </button>
        )}
      </div>

      {tableMissing && (
        <div className="panel">
          <div className="panel-title">Resources Setup Needed</div>
          <p style={{ color: 'var(--charcoal-light)', fontSize: '14px', lineHeight: 1.6 }}>
            The Resources tab is ready, but the shared resources table needs to be created in Supabase once.
          </p>
          {isCoach ? (
            <>
              <div style={{ color: 'var(--gold-light)', fontFamily: 'Cinzel, serif', fontSize: '13px', letterSpacing: '2px', textTransform: 'uppercase', marginTop: '1rem', marginBottom: '8px' }}>
                Supabase SQL
              </div>
              <pre style={{ whiteSpace: 'pre-wrap', background: 'rgba(0,0,0,0.22)', border: '1px solid var(--border)', borderRadius: '4px', padding: '12px', color: 'var(--bone)', fontSize: '12px', lineHeight: 1.45, overflowX: 'auto' }}>
                {SETUP_SQL}
              </pre>
            </>
          ) : (
            <p style={{ color: 'var(--charcoal-light)', fontSize: '14px', lineHeight: 1.6 }}>
              Check back soon. Your coach is setting up the resource library.
            </p>
          )}
        </div>
      )}

      {isCoach && showForm && !tableMissing && (
        <div className="panel" style={{ marginBottom: '1.5rem' }}>
          <div className="panel-title">Add Resource</div>
          <div className="field"><label>Title</label><input value={title} onChange={e => setTitle(e.target.value)} placeholder="Vacation Workouts" /></div>
          <div className="field"><label>Link</label><input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://..." /></div>
          <div className="field"><label>Description</label><textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="What athletes should know before opening this." /></div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
            <div className="field">
              <label>Type</label>
              <select value={resourceType} onChange={e => setResourceType(e.target.value)}>
                {RESOURCE_TYPES.map(type => <option key={type} value={type}>{type}</option>)}
              </select>
            </div>
            <div className="field">
              <label>Category</label>
              <select value={category} onChange={e => setCategory(e.target.value)}>
                {RESOURCE_CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
              </select>
            </div>
          </div>
          <button className="btn-primary" onClick={addResource} disabled={!title.trim() || !url.trim()}>Save Resource</button>
        </div>
      )}

      {loading && !tableMissing && <div className="loading">Loading...</div>}

      {!loading && !tableMissing && resources.length === 0 && (
        <div className="empty">
          <h3>No resources yet</h3>
          <p>{isCoach ? 'Add your first resource above.' : 'Your coach has not posted resources yet.'}</p>
        </div>
      )}

      {!tableMissing && Object.entries(grouped).map(([cat, items]) => (
        <div key={cat} className="panel" style={{ marginBottom: '1.25rem' }}>
          <div className="panel-title">{cat}</div>
          <div style={{ display: 'grid', gap: '10px' }}>
            {items.map(resource => (
              <div key={resource.id} className="class-card" style={{ marginBottom: 0 }}>
                <div className="class-card-header">
                  <a
                    href={resource.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', color: 'inherit', textDecoration: 'none', flex: 1 }}
                  >
                    <span style={{ width: '34px', height: '34px', border: '1px solid var(--gold-dark)', borderRadius: '4px', color: 'var(--gold-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {resourceIcon(resource.resource_type)}
                    </span>
                    <span>
                      <span style={{ display: 'block', fontFamily: 'Cinzel, serif', color: 'var(--gold-light)', fontSize: '16px', letterSpacing: '1px' }}>
                        {resource.title}
                      </span>
                      <span style={{ display: 'block', color: 'var(--charcoal-light)', fontSize: '12px', letterSpacing: '1px', textTransform: 'uppercase', marginTop: '3px' }}>
                        {resource.resource_type || 'Link'}
                      </span>
                      {resource.description && (
                        <span style={{ display: 'block', color: 'var(--charcoal-light)', fontSize: '14px', lineHeight: 1.5, marginTop: '8px' }}>
                          {resource.description}
                        </span>
                      )}
                    </span>
                  </a>
                  {isCoach && (
                    <button className="btn-ghost" style={{ color: 'var(--rose)', fontSize: '10px' }} onClick={() => removeResource(resource.id)}>
                      Remove
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {toast && <div className="toast">{toast}</div>}
    </div>
  )
}
